import { useState, useEffect } from 'react';
import { Container, Grid, IconButton, Tooltip } from '@material-ui/core';
import { format, differenceInDays, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import clsx from 'clsx';

import { userService } from '#root/services';
import { useNotification } from '#root/hooks';
import { PageHeader } from '#root/components/ui/composed/page-header/page-header';
import { GuveryPaper, GuveryEmpty } from '#root/ui-components/cards';
import { Coupons } from '#root/assets';
import { CopyIcon, TicketIcon } from '#root/icons';
import CouponInfoPanel from './CouponInfoPanel';
import styles from './userCoupons.module.scss';
import './userCoupons.scss';

// Types
interface CouponData {
  service: string;
  name: string;
  description?: string;
  type: string;
  type_factor: string;
  type_field: string;
  type_limit: string;
  service_product?: string;
  max_discount_quantity: number;
  consumed_quantity: number | null;
}

interface CouponUser {
  code: string;
  current_status: string;
  expiration_date: string | null;
  consumed_at: string | null;
  discounted_at: string | null;
  coupon: CouponData;
}

const CouponStatus = {
  created: 'CREATED',
  consumed: 'CONSUMED',
  discounted: 'DISCOUNTED',
};

const CouponService: Record<string, string> = {
  'GUVERY REGULAR': 'regular',
  'GUVERY ONE': 'one',
};

// Helper para parsear fechas de la API
const parseApiDate = (dateString: string | null): Date | null => {
  if (!dateString) return null;
  const normalized = dateString.replace(/ /g, 'T');
  return parseISO(normalized);
};

// Helper para verificar si un cupón está vencido por fecha
const isCouponExpired = (expirationDate: string | null): boolean => {
  if (!expirationDate) return false;
  const expDate = parseApiDate(expirationDate);
  if (!expDate) return false;
  return expDate < new Date();
};

// Helper para obtener prioridad de ordenamiento de cupón
// Orden: Activos (0) → Usados (1) → Vencidos (2)
const getCouponSortPriority = (coupon: CouponUser): number => {
  const isExpired = isCouponExpired(coupon.expiration_date);
  const isUsed = coupon.current_status !== CouponStatus.created;

  if (isUsed) return 1; // Usados después de activos
  if (isExpired) return 2; // Vencidos al final
  return 0; // Activos primero
};

// Componente interno para mostrar el contenido del cupón según su estado
interface ContentProps {
  status: string;
  expirationDate: string | null;
  description?: string;
  maxQuantity: number;
  consumedQuantity: number | null;
  consumedAt: string | null;
  discountedAt: string | null;
  isExpired: boolean;
}

const CouponContent = ({
  status,
  expirationDate,
  description,
  maxQuantity,
  consumedQuantity,
  consumedAt,
  discountedAt,
  isExpired,
}: ContentProps) => {
  const expDate = parseApiDate(expirationDate);

  // Cupón vencido por fecha (pero status sigue siendo CREATED)
  if (status === CouponStatus.created && isExpired && expDate) {
    return (
      <p className={styles.expiredText}>
        Venció el:{' '}
        <span>{format(expDate, "dd '/' MMM '/' yyyy", { locale: es })}</span>
      </p>
    );
  }

  // Cupón activo (CREATED y no vencido)
  if (status === CouponStatus.created) {
    const qty = consumedQuantity ?? 0;
    const unlimitedCoupon = maxQuantity === 0;
    const couponQty = maxQuantity - qty;

    return (
      <>
        {expDate && (
          <p className={styles.detail}>
            Valido hasta:{' '}
            <span className="guv-semibold guv-text-base">
              {format(expDate, "dd '/' MMM '/' yyyy", { locale: es })}
            </span>
          </p>
        )}
        {!unlimitedCoupon && (
          <p
            className="guv-font-size-7 guv-text-alt-2"
            style={{ marginTop: description ? 8 : 24 }}>
            {couponQty > 0 ? (
              `Solo quedan ${couponQty} cupones en la comunidad. Utilízalo antes que se agote`
            ) : (
              <span className="guv-text-error">
                Ya no puedes utilizar este cupón, lo sentimos...
              </span>
            )}
          </p>
        )}
      </>
    );
  }

  if (status === CouponStatus.consumed && consumedAt) {
    const consumedDate = parseApiDate(consumedAt);
    return (
      <p className={styles.detail}>
        Cupón utilizado el:{' '}
        <span className="guv-semibold guv-text-base">
          {consumedDate && format(consumedDate, "dd '/' MMM '/' yyyy", { locale: es })}
        </span>
      </p>
    );
  }

  if (discountedAt) {
    const discountedDate = parseApiDate(discountedAt);
    return (
      <p className={styles.detail}>
        Cupón aplicado el:{' '}
        <span className="guv-semibold guv-text-base">
          {discountedDate && format(discountedDate, "dd '/' MMM '/' yyyy", { locale: es })}
        </span>
      </p>
    );
  }

  return null;
};

function UserCoupons() {
  const { createMessage } = useNotification();
  const [loading, setLoading] = useState(true);
  const [couponsArray, setCouponsArray] = useState<CouponUser[]>([]);

  const copyToClipboard = (code: string) => {
    navigator.clipboard.writeText(code);
    createMessage.success('Código copiado: ' + code);
  };

  const fetchUserCoupons = async () => {
    setLoading(true);
    const res = await userService.coupons();
    if (!res.error) {
      setCouponsArray(res.data.coupon_user);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchUserCoupons();
  }, []);

  // Calcular subtitle con conteo y vencimientos próximos
  const getSubtitle = () => {
    if (loading || couponsArray.length === 0) return undefined;

    // Solo contar como "disponibles" los que están CREATED Y no vencidos por fecha
    const activeCoupons = couponsArray.filter(
      (c) => c.current_status === CouponStatus.created && !isCouponExpired(c.expiration_date)
    );
    const total = activeCoupons.length;

    if (total === 0) {
      return `${couponsArray.length} cupón${couponsArray.length !== 1 ? 'es' : ''} (ninguno disponible)`;
    }

    const totalText = `${total} cupón${total !== 1 ? 'es' : ''} disponible${total !== 1 ? 's' : ''}`;

    // Contar cupones que vencen en los próximos 7 días
    const today = new Date();
    const expiringSoon = activeCoupons.filter((c) => {
      if (!c.expiration_date) return false;
      const expDate = parseApiDate(c.expiration_date);
      if (!expDate) return false;
      const daysUntilExpiry = differenceInDays(expDate, today);
      return daysUntilExpiry >= 0 && daysUntilExpiry <= 7;
    }).length;

    if (expiringSoon === 0) {
      return totalText;
    }

    return (
      <>
        {totalText}
        <span className="subtitleAlert">
          {` · ${expiringSoon} vence${expiringSoon !== 1 ? 'n' : ''} pronto`}
        </span>
      </>
    );
  };

  return (
    <div className="user-coupons guv-main-container">
      <Container maxWidth="lg">
        <PageHeader title="Mis Cupones" subtitle={getSubtitle()} />
        {!loading && <CouponInfoPanel />}
        {loading && <h4>Cargando...</h4>}
        {!loading && (
          <Grid container spacing={3}>
            {[...couponsArray].sort((a, b) => getCouponSortPriority(a) - getCouponSortPriority(b)).map((item, index) => {
              const itemService = item?.coupon?.service;
              const serviceClass =
                CouponService[itemService] !== undefined
                  ? CouponService[itemService]
                  : 'regular';
              const isExpired = isCouponExpired(item.expiration_date);
              const isInactive = item.current_status !== CouponStatus.created || isExpired;

              const isActive = item.current_status === CouponStatus.created && !isExpired;

              return (
                <Grid item key={index} xs={12} sm={10} md={6}>
                  <GuveryPaper
                    disableHover
                    disableElevation
                    className={clsx(
                      styles.card,
                      isActive && styles.cardActive,
                      isInactive && styles.cardDisable
                    )}>
                    <div className={clsx(styles.badge, styles[serviceClass])}>
                      <span className={styles.badgeText}>{item?.coupon?.service}</span>
                    </div>
                    {/* Badge con código y botón copiar para cupones activos */}
                    {isActive && (
                      <div className={styles.codeBadge}>
                        <span className={styles.codeText}>{item.code}</span>
                        <Tooltip title="Copiar código">
                          <IconButton
                            size="small"
                            className={styles.copyButton}
                            onClick={() => copyToClipboard(item.code)}>
                            <CopyIcon />
                          </IconButton>
                        </Tooltip>
                      </div>
                    )}
                    {/* Badge USADO para cupones consumidos/descontados */}
                    {item.current_status !== CouponStatus.created && (
                      <div className={styles.usedBadge}>USADO</div>
                    )}
                    {/* Badge VENCIDO para cupones expirados por fecha (solo si no está usado) */}
                    {isExpired && item.current_status === CouponStatus.created && (
                      <div className={styles.expiredBadge}>VENCIDO</div>
                    )}
                    <div className={styles.content}>
                      <div className={styles.left}>
                        <h2>
                          {item?.coupon?.type_factor}
                          <b>{item?.coupon?.type === 'porcentaje' ? '%' : '$'}</b>
                        </h2>
                        <p>Descuento sobre</p>
                        <p>
                          {item.coupon?.type_field === 'acuerdo_comguv'
                            ? 'Comisión de Guvery '
                            : 'Importe del Envío'}
                        </p>
                        <span className={styles.max}>(máx. $ {item.coupon.type_limit})</span>
                      </div>
                      <div className={styles.right}>
                        <div className={styles.name}>
                          <TicketIcon />
                          <h6>{item.coupon?.name}</h6>
                        </div>
                        {item?.coupon?.description && (
                          <div className={styles.desc}>
                            <p>{item.coupon?.description}</p>
                          </div>
                        )}
                        {item?.coupon?.service_product && (
                          <p className={clsx(styles.detail, styles.space)}>
                            Aplica sólo para el producto:{' '}
                            <span className="guv-semibold guv-text-base">
                              {item.coupon?.service_product}
                            </span>
                          </p>
                        )}
                        <CouponContent
                          status={item.current_status}
                          expirationDate={item.expiration_date}
                          description={item.coupon?.description}
                          maxQuantity={item.coupon?.max_discount_quantity}
                          consumedQuantity={item.coupon?.consumed_quantity}
                          consumedAt={item.consumed_at}
                          discountedAt={item.discounted_at}
                          isExpired={isExpired}
                        />
                      </div>
                    </div>
                  </GuveryPaper>
                </Grid>
              );
            })}
            {couponsArray.length === 0 && (
              <Grid item xs={12}>
                <GuveryEmpty description="No tienes ningún cupón" image={Coupons} />
              </Grid>
            )}
          </Grid>
        )}
      </Container>
    </div>
  );
}

export default UserCoupons;
